<template>
    <table :id="tableId" class="table table-centered table-striped"></table>
</template>

<script>
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from 'datatables.net';

export default {
    name: 'CustomDataTable',
    props: {
        tableId: {
            type: String,
            required: true
        },
        columns: {
            type: Array,
            required: true
        },
        emptyTableLabel: {
            type: String,
            required: true
        },
        data: {
            type: Array,
        },
        rowClick: {
            type: Function,
        },
        rowClickId: {
            type: String,
            default: '.row-click'
        },
        searchMandatory: {
            type: Boolean,
            default: false
        },
        searchInputId: {
            type: String,
            default: 'searchTrigger'
        },
    },
    setup(props, { expose }) {

        expose({
            refresh,
        });

        const { t, locale } = useI18n();
        let datatable;
        const searchTimer = ref(null);
        let cachedData = props.data ? [...props.data] : [];

        function getLanguage() {
            return {
                "sProcessing":     t('datatables.processing'),
                "sSearch":         t('datatables.search') + "&nbsp;:",
                "sLengthMenu":     t('datatables.lengthMenu'),
                "sInfo":           t('datatables.info'),
                "sInfoEmpty":      t('datatables.infoEmpty'),
                "sInfoFiltered":   t('datatables.infoFiltered'),
                "sInfoPostFix":    "",
                "sLoadingRecords": t('datatables.loadingRecords'),
                "sZeroRecords":    `<div style='text-align:center;'>${t('datatables.zeroRecords')}</div>`,
                "sEmptyTable":     props.emptyTableLabel,
                "oPaginate": {
                    "sFirst":      t('datatables.first'),
                    "sPrevious":   "<i class='mdi mdi-chevron-left'>",
                    "sNext":       "<i class='mdi mdi-chevron-right'>",
                    "sLast":       t('datatables.last')
                },
                "searchPlaceholder": t('datatables.search'),
                "emptyTable": `<div style='text-align:center;'>${props.emptyTableLabel}</div>`,
                "paginate": {
                    "previous": "<i class='mdi mdi-chevron-left'>",
                    "next": "<i class='mdi mdi-chevron-right'>"
                }
            };
        }

        function init() {
            const table = document.getElementById(props.tableId);

            datatable = new DataTable(table, {
                data: cachedData,
                language: getLanguage(),
                dom: 'lrtip',
                order: [[0, 'asc']],
                pageLength: 50,
                lengthChange: false,
                autoWidth: false,
                columns: props.columns,
                colReorder: false,
                paging: false,
                info: false,
                drawCallback: function () {
                    const tableWrapper = table.closest('.dt-container');
                    if (tableWrapper) {
                        const pagination = tableWrapper.querySelector('.dt-paging nav');
                        if (pagination) {
                            pagination.classList.add('d-flex', 'justify-content-end', 'pagination-rounded');
                        }
                    }
                },
                headerCallback: function (thead) {
                    const ths = thead.getElementsByTagName('th');
                    for (let i = 0; i < ths.length; i++) {
                        ths[i].innerHTML = ths[i].textContent;
                    }
                },
                rowCallback: function (row, data) {
                    if (data.color) {
                        for (const cell of row.cells) {
                            cell.style.setProperty('color', data.color, 'important');
                        }
                    }
                },
            });

            const searchEl = document.getElementById(props.searchInputId);
            if(searchEl) {
                searchEl.addEventListener('keyup', function () {
                    if(searchTimer.value) {
                        clearTimeout(searchTimer.value);
                    }
                    const value = this.value;
                    searchTimer.value = setTimeout(() => {
                        datatable.search(value).draw();
                    }, 250);
                });
            }
        }

        function reinit() {
            if (datatable) {
                cachedData = datatable.data().toArray();
                datatable.destroy();
                datatable = null;
                const table = document.getElementById(props.tableId);
                if (table) {
                    table.innerHTML = '';
                }
            }
            init();
        }

        function handleClickEvent(e) {
            const button = e.target.closest(props.rowClickId);
            if (button) {
                e.preventDefault();
                e.stopPropagation();
                const action = button.getAttribute('row-action');
                const id = button.getAttribute('row-id');
                props.rowClick(action, Number(id));
            }
        }

        function handleChangeEvent(e) {
            const checkbox = e.target.closest('.row-checked');
            if (checkbox) {
                const action = checkbox.getAttribute('row-action');
                const id = checkbox.getAttribute('row-id');
                const isChecked = checkbox.checked;
                props.rowClick(action, Number(id), isChecked);
                e.preventDefault();
                checkbox.checked = !isChecked;
            }
        }

        onMounted(() => {
            init();
            const table = document.getElementById(props.tableId);
            if (table) {
                table.addEventListener('click', handleClickEvent);
                table.addEventListener('change', handleChangeEvent);
            }
        });

        onUnmounted(() => {
            if (datatable) {
                datatable.destroy();
            }
            const table = document.getElementById(props.tableId);
            if (table) {
                table.removeEventListener('click', handleClickEvent);
                table.removeEventListener('change', handleChangeEvent);
            }
        });

        watch(locale, () => {
            reinit();
            const table = document.getElementById(props.tableId);
            if (table) {
                table.addEventListener('click', handleClickEvent);
                table.addEventListener('change', handleChangeEvent);
            }
        });

        function refresh(rows) {
            cachedData = rows ? [...rows] : [];
            if (datatable) {
                datatable.clear().rows.add(rows).draw();
            }
        }
    },
}

</script>
